import {
  type RentalManagerUserPropertyMultiplexWebApiHandler_delete_Args,
  type RentalManagerUserPropertyMultiplexWebApiHandler_delete_Options,
  useRentalManagerUserPropertyMultiplexWebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type { UseMutationOptions } from '@zg-rentals/react-query5';
import type { ApiResponseErrorDto } from '@zg-rentals/ts-rental-manager-api-client';

/**
 * Same shape as `UserPropertyMultiplexPhotoResponseWebDto` from the generated API client, sourced from the multiplex
 * handler `delete` mutation result type.
 */
export type UserPropertyMultiplexPhotoResponseWebDto =
  RentalManagerUserPropertyMultiplexWebApiHandler_delete_Options extends UseMutationOptions<
    infer Data,
    unknown,
    RentalManagerUserPropertyMultiplexWebApiHandler_delete_Args
  >
    ? Data
    : never;

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

/**
 * Runtime guard beyond compile-time args:
 * `getParams.multiplexBuildingSessionId` and `getParams.photoId` must be non-empty strings.
 */
const assertMultiplexPhotoDeleteMutationArgs = (
  args: RentalManagerUserPropertyMultiplexWebApiHandler_delete_Args,
): void => {
  const { getParams } = args;

  if (typeof getParams !== 'object' || getParams === null) {
    throw new TypeError('useMultiplexPhotoDelete: `getParams` is required (DeleteParams).');
  }

  const sessionId = getParams.multiplexBuildingSessionId;
  const photoId = getParams.photoId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexPhotoDelete: `getParams.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (typeof photoId !== 'string' || photoId.trim() === '') {
    throw new TypeError('useMultiplexPhotoDelete: `getParams.photoId` is required on every call (non-empty string).');
  }
};

export type UseMultiplexPhotoDeleteOptions = Omit<
  RentalManagerUserPropertyMultiplexWebApiHandler_delete_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexPhotoDeleteMutation = ReturnType<typeof useRentalManagerUserPropertyMultiplexWebApiHandler_delete>;

export type UseMultiplexPhotoDeleteResult = Omit<
  BaseMultiplexPhotoDeleteMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_delete_Args,
    options?: Parameters<BaseMultiplexPhotoDeleteMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexPhotoDeleteMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_delete_Args,
    options?: Parameters<BaseMultiplexPhotoDeleteMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexPhotoDeleteMutation['mutateAsync']>;
  data: BaseMultiplexPhotoDeleteMutation['data'];
  error: BaseMultiplexPhotoDeleteMutation['error'];
  isPending: BaseMultiplexPhotoDeleteMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  success: boolean | undefined;
  errorMessage: UserPropertyMultiplexPhotoResponseWebDto['errorMessage'] | undefined;
  units: UserPropertyMultiplexPhotoResponseWebDto['units'] | undefined;
};

/**
 * Multiplex photo delete: wraps the generated `delete` mutation (DELETE `/api/web/v1/users/properties/multiplex/photos/delete`).
 *
 * Merges default `meta`: invalidates tag `multiplexBuildingSessionDetails` (plus any `meta.invalidates` you pass) and
 * sets `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), and `success`, `errorMessage`, and `units` from the last
 * successful `data`.
 *
 * **Calling the mutation:** Use `{ getParams: { multiplexBuildingSessionId, photoId } }`. Compile-time typing applies
 * to `mutate` / `mutateAsync`; runtime checks enforce non-empty session id and photo id strings.
 */
const useMultiplexPhotoDelete = (
  mutationOptions: UseMultiplexPhotoDeleteOptions = {},
): UseMultiplexPhotoDeleteResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexWebApiHandler_delete({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexWebApiHandler_delete',
        details: 'useMultiplexPhotoDelete',
        ...metaOverrides?.context,
      },
    },
  });

  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexPhotoDeleteMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexPhotoDeleteMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    success: data?.success,
    errorMessage: data?.errorMessage,
    units: data?.units,
  };
};

export default useMultiplexPhotoDelete;
